import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  Panel,
  PanelBody,
  PanelHeader,
  PanelFooter,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {useApi} from 'src/hooks/useApi';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  selectFilteredJobList,
  selectJobInterviewFlow,
  selectHasCrmPermission,
  selectUserInfo,
} from 'src/selectors/crm';
import {getFilteredJob} from 'src/action-creators/crm-new';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import CustomForm from 'src/components/crm/jobs/crud/job-details/custom-form/custom-form';

import {
  Toast,
  ToastTitle,
  toastApi,
  TOAST_SEMANTIC,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {
  BodySmall,
  SubTitleSmall,
  SubTitleLarge,
  FormInputSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {useSelector, useDispatch} from 'react-redux';
import * as reduxApi from 'src/utils/redux-api-v2';
import {useInflight} from 'src/hooks/redux';
import {debounce} from 'lodash';
import {SimpleTypeahead} from '@spaced-out/ui-design-system/lib/components/Typeahead';
import {SimpleDropdown} from '@spaced-out/ui-design-system/lib/components/Dropdown';
import {Textarea} from '@spaced-out/ui-design-system/lib/components/Textarea';
import {Rating} from '@spaced-out/ui-design-system/lib/components/Rating';
import {FileUpload} from '@spaced-out/ui-design-system/lib/components/FileUpload';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {
  RadioGroup,
  RadioButton,
} from '@spaced-out/ui-design-system/lib/components/RadioButton';
import {getInterviewFlow} from 'src/action-creators/crm';
import css from './add-referrals-panel.css';
import {CRM_PERMISSIONS} from 'src/utils/crm-new/common';
import {TypeAheadSelector} from 'src/components/crm/lib/typeAhead/typeahead';


type Props = {
  isOpen: boolean;
  handleClose?: () => void;
};

type FORMDATA = {
  name: string;
  email: string;
  phone: string;
  note: string;
  jobId: string;
  known: string;
  acquaintance: string;
  resume: File;
  customFieldValuesForm: {
    formId: string;
  };
};

const AddReferralsPanel = ({isOpen, handleClose}: Props) => {
  const {t} = useLabelTranslation();
  const userInfo = useSelector(selectUserInfo);
  const REFER_TO_JOB = t('Refer_to_a_Job', 'Refer to a Job');
  const REFER_WITHOUT_JOB = t('Refer_without_a_Job', 'Refer without a Job');
  const [selectedOption, setSelectedOption] = React.useState(REFER_TO_JOB);
  const dispatch = useDispatch();
  const interviewFlowData = useSelector(selectJobInterviewFlow);
  const filteredJobList = useSelector(selectFilteredJobList);
  const [loading, setLoading] = React.useState(false);
  const [searchJobTextOrCode, setSearchJobTextOrCode] = React.useState('');
  const [isCandidateKnown, setIsCandidateKnown] = React.useState<
    string | null | undefined
  >(null);
  const [rating, setRating] = React.useState<any>();
  const [selectedJobId, setSelectedJobId] = React.useState<string>('');
  const [selectedStageId, setSelectedStageId] = React.useState('');
  const [answers, setAnswers] = React.useState([]);
  const [candidateName, setCandidateName] = React.useState('');
  const [candidateEmail, setCandidateEmail] = React.useState('');
  const [candidatePhone, setCandidatePhone] = React.useState('');
  const [note, setNote] = React.useState('');
  const [attachingFile, setAttachingFile] = React.useState(false);
  const [resume, setResume] = React.useState<any>(null);
  const isInterviwdataLoading = useInflight(getInterviewFlow(selectedJobId));
  const [customUserFieldValues, setCustomUserFieldValues] = React.useState([]);
  const [isCustomUserFieldValid, setIsCustomUserFieldValid] =
    React.useState(false);
  const referCandidateWithouJob = selectHasCrmPermission(
    userInfo,
    CRM_PERMISSIONS.REFER_CANDIDATE_WITHOUT_JOB,
  );

  React.useEffect(() => {
    if (selectedJobId) {
      dispatch(getInterviewFlow(selectedJobId));
    }
  }, [selectedJobId, dispatch]);
  const {
    result: customeFormOfSections,
    isLoading: customeFormOfSectionsLoading,
  } = useApi(`/api/custom-form?formOf=REFERRAL`, {
    apiPath: '/trmapi',
    deps: [],
  });

  // @ts-expect-error - TS7006 - Parameter 'componentState' implicitly has an 'any' type.
  const generateReferralPayload = (componentState) => {
    const {
      candidateName,
      candidateEmail,
      candidatePhone,
      selectedJobId,
      selectedOption,
      isCandidateKnown,
      note,
      answers,
      resume,
      customeFormOfSections,
    } = componentState;

    const formData = new FormData();

    // Basic candidate details
    formData.append('name', candidateName);
    formData.append('email', candidateEmail);

    // Optional fields
    if (candidatePhone) {
      formData.append('phone', candidatePhone);
    }

    if (note) {
      formData.append('note', note);
    }

    // Job referral specific
    if (selectedOption === 'Refer to a Job' && selectedJobId) {
      formData.append('jobId', selectedJobId.toString());
    }

    // Candidate known status
    formData.append('known', String(isCandidateKnown));
    formData.append('acquaintance', String(isCandidateKnown));

    // Get form ID from custom form sections
    const formId = customeFormOfSections?.[0]?.id || '';
    formData.append('customFieldValuesForm.formId', formId);

    // Handle custom field answers
    if (answers && answers.length > 0) {
      // @ts-expect-error - TS7006 - Parameter 'answer' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type.
      answers.forEach((answer, index) => {
        if (!answer.questionId || !answer.answer) {
          return;
        }

        // Add to customFieldValues array
        formData.append(
          `customFieldValuesForm.customFieldValues[${index}].fieldId`,
          answer.questionId.toString(),
        );
        formData.append(
          `customFieldValuesForm.customFieldValues[${index}].value`,
          answer.answer,
        );

        // Also add as individual custom fields
        formData.append(`custom_${answer.questionId}`, answer.answer);
      });
    }

    // Handle resume upload
    if (resume) {
      formData.append('resume', resume[0].file);
    }

    return formData;
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      const formData = generateReferralPayload({
        candidateName,
        candidateEmail,
        candidatePhone,
        selectedJobId,
        selectedOption,
        isCandidateKnown,
        note,
        answers,
        resume,
        customeFormOfSections,
      });

      await dispatch(
        reduxApi.post(
          `api/employee-candidates`,
          formData,
          {},
          {apiPath: '/trmapi/'},
        ),
      );
      const successToast = (
        <Toast initialFocus={0} semantic="success">
          <ToastTitle>
            {t(
              'Referral_submitted_successfully',
              'Referral submitted successfully',
            )}
          </ToastTitle>
        </Toast>
      );
      toastApi.show(successToast);
      handleClose?.();
      setLoading(false);

      // Success handling...
    } catch (error: any) {
      console.log('error', error);
      const errorToast = (
        <Toast initialFocus={0} semantic="danger">
          <ToastTitle>
            {t('Failed_to_submit_referral', 'Failed to submit referral')}
          </ToastTitle>
        </Toast>
      );
      toastApi.show(errorToast);
      setLoading(false);
    }
  };

  const handleAttachFile = async (questionId: any, files: any) => {
    try {
      const formData = new FormData();
      formData.append('file', files[0].file);
      setAttachingFile(true);
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.information}>
          <ToastTitle>
            {t('File_Attachment_initiated', 'File Attachment initiated!')}
          </ToastTitle>
        </Toast>,
      );

      // Pass the formData directly without spreading it
      const response = await dispatch(
        reduxApi.post(
          `api/upload-attachment/v1`,
          formData,
          {},
          {apiPath: '/trmapi/'},
        ),
      );

      handleAnswerChange(questionId, response.filekey);
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.success}>
          <ToastTitle>{`${t(
            'File_Attachment_successful',
            'File Attachment successful',
          )}!`}</ToastTitle>
        </Toast>,
      );
    } catch (e: any) {
      console.error(e);
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.danger}>
          <ToastTitle>{`${t(
            'File_Attachment_failed',
            'File Attachment failed',
          )}!`}</ToastTitle>
        </Toast>,
      );
    } finally {
      setAttachingFile(false);
    }
  };

  const handleOnChange = (
    values: Array<{
      fieldId: number;
      fieldType?: string;
      value: string | null;
    }>,
    isValid: boolean,
    errors: Array<{
      error: string;
      fieldId: number;
    }>,
  ) => {
    // @ts-expect-error - TS2345 - Argument of type '{ fieldId: number; fieldType?: string | undefined; value: string | null; }[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
    setCustomUserFieldValues(values);
    setIsCustomUserFieldValid(isValid);

    // Update answers state with the new values
    const newAnswers = values
      .filter(
        (value) =>
          value &&
          typeof value.fieldId === 'number' &&
          value.value !== null &&
          value.value !== undefined,
      )
      .map((value) => ({
        questionId: value.fieldId,
        answer: String(value.value),
      }));
    // @ts-expect-error - TS2345 - Argument of type '{ questionId: number; answer: string; }[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
    setAnswers(newAnswers);
  };

  const handleAnswerChange = (questionId: string, value: string) => {
    // @ts-expect-error - TS2345 - Argument of type '(prevAnswers: never[]) => { questionId: string; answer: string; }[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
    setAnswers((prevAnswers) => {
      const existingAnswerIndex = prevAnswers.findIndex(
        // @ts-expect-error - TS2339 - Property 'questionId' does not exist on type 'never'.
        (answer) => answer.questionId === questionId,
      );

      if (existingAnswerIndex >= 0) {
        // Update existing answer
        const updatedAnswers = [...prevAnswers];
        // @ts-expect-error - TS2322 - Type '{ questionId: string; answer: string; }' is not assignable to type 'never'.
        updatedAnswers[existingAnswerIndex] = {
          questionId,
          answer: value.toString(),
        };
        return updatedAnswers;
      } else {
        // Add new answer
        return [
          ...prevAnswers,
          {
            questionId,
            answer: value.toString(),
          },
        ];
      }
    });
  };

  const handleRadioChange = (value: string) => {
    setSelectedOption(value);
  };

  const handleCandidateKnownChange = (value: string) => {
    setIsCandidateKnown(value);
  };

  const filteredJobListOptions = React.useMemo(() => {
    // @ts-expect-error - TS2339 - Property 'content' does not exist on type 'HIERARCHY_LEVEL[]'.
    return filteredJobList?.content?.map((job: any) => ({
      key: job.id,
      label: job.title + (job.code ? `(${t('ID', 'ID')}: ${job.code})` : ''),
      secondaryLabel: job.location ? job.location : null,
    }));
  }, [filteredJobList]);

  const tempFilter = React.useMemo(
    () => ({
      publicAccess: true,
      titleOrCode: searchJobTextOrCode,
      templateName: '',
      pageSize: 50,
      page: 0,
      jobStatuses: ['OPEN'],
      referralActive: true,
      template: false,
    }),
    [searchJobTextOrCode],
  );

  const debouncedJobSearch = React.useCallback(
    debounce(() => {
      dispatch(getFilteredJob({...tempFilter}));
    }, 300),
    [dispatch, tempFilter],
  );

  React.useEffect(() => {
    debouncedJobSearch();
  }, [tempFilter, debouncedJobSearch]);

  const isFormValid = React.useMemo(() => {
    // Basic required fields validation
    if (!candidateName || !candidateEmail) {
      return false;
    }

    // Validate job selection if "Refer to a Job" is selected
    if (selectedOption === REFER_TO_JOB && !selectedJobId) {
      return false;
    }

    // Validate candidate known selection
    if (isCandidateKnown === null) {
      return false;
    }
    // Validate custom fields - only check if custom fields exist
    const hasCustomFields =
      Array.isArray(customeFormOfSections) &&
      customeFormOfSections.length > 0 &&
      customeFormOfSections[0]?.fields?.length > 0;
    if (hasCustomFields && !isCustomUserFieldValid) {
      return false;
    }

    // Validate resume file
    if (!resume) {
      return false;
    }

    // Validate mandatory custom fields
    const mandatoryCustomFieldIds =
      // @ts-expect-error - TS2339 - Property 'length' does not exist on type '{}'.
      customeFormOfSections && customeFormOfSections.length > 0
        ? // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type '0' can't be used to index type '{}'.
          customeFormOfSections[0].fields
            .filter(
              // @ts-expect-error - TS7006 - Parameter 'field' implicitly has an 'any' type.
              (field) => field.showStatus === 'MANDATORY' && !field.default,
            )
            // @ts-expect-error - TS7006 - Parameter 'field' implicitly has an 'any' type.
            .map((field) => field.customFieldId)
        : [];

    const answeredMandatoryFieldIds = answers
      // @ts-expect-error - TS2339 - Property 'answer' does not exist on type 'never'. | TS2339 - Property 'answer' does not exist on type 'never'.
      .filter((answer) => answer.answer && answer.answer.trim() !== '')
      // @ts-expect-error - TS2339 - Property 'questionId' does not exist on type 'never'.
      .map((answer) => answer.questionId);

    // @ts-expect-error - TS7006 - Parameter 'id' implicitly has an 'any' type.
    const allMandatoryFieldsAnswered = mandatoryCustomFieldIds.every((id) =>
      answeredMandatoryFieldIds.includes(id),
    );

    return allMandatoryFieldsAnswered;
  }, [
    candidateName,
    candidateEmail,
    selectedOption,
    selectedJobId,
    isCandidateKnown,
    customeFormOfSections,
    answers,
    resume,
    isCustomUserFieldValid,
  ]);

  return (
    <Panel
      anchor="right"
      initialFocus={1}
      onClose={handleClose}
      size="large"
      tapOutsideToClose
      isOpen={isOpen}
      hideBackdrop={false}
    >
      <PanelHeader onCloseButtonClick={handleClose} size="medium">
        {t('Add_a_referral', 'Add a referral')}
      </PanelHeader>
      {customeFormOfSectionsLoading ? (
        <CircularLoader className={css.loader} size="large" />
      ) : (
        <PanelBody className={css.panelBody}>
          {referCandidateWithouJob && (
            <RadioGroup
              name=""
              align="horizontal-fluid"
              errorText={`*${t(
                'A_selection_must_be_made_to_continue',
                'A selection must be made to continue',
              )}`}
              value={selectedOption}
              onChange={handleRadioChange}
            >
              <RadioButton value={REFER_TO_JOB}>{REFER_TO_JOB}</RadioButton>
              <RadioButton value={REFER_WITHOUT_JOB}>
                {REFER_WITHOUT_JOB}
              </RadioButton>
            </RadioGroup>
          )}
          {selectedOption === REFER_TO_JOB && (
            <SimpleTypeahead
              label={t('Select_a_Job', 'Select a Job')}
              placeholder={t(
                'Search_by_Job_Title_or_Code',
                'Search by Job Title or Code',
              )}
              typeaheadInputText={searchJobTextOrCode}
              options={filteredJobListOptions}
              onSearch={(e) => setSearchJobTextOrCode(e.target.value)}
              onSelect={(option) => {
                setSelectedJobId(option.key);
              }}
              required
              onClear={() => setSelectedJobId('')}
              menuOpenOffset={0}
            />
          )}
          {selectedOption === REFER_TO_JOB && selectedJobId !== '' && (
            <SimpleDropdown
              label={t('Stage', 'Stage')}
              placeholder={t('Select_Stage', 'Select Stage')}
              options={interviewFlowData?.stages
                .slice(0, -1)
                .map((stage: any) => ({
                  key: stage.id.toString(),
                  label: stage.name,
                }))}
              selectedKeys={selectedStageId ? [selectedStageId.toString()] : []}
              onChange={(option) => setSelectedStageId(option.key)}
            />
          )}
          <Input
            name="name"
            placeholder={t('Enter_Candidate_Name', 'Enter Candidate Name')}
            label={t('Candidate_Name', 'Candidate Name')}
            value={candidateName}
            required
            onChange={(e) => setCandidateName(e.target.value)}
          />
          <div className={css.gap}>
            <Input
              name="email"
              placeholder={t('Enter_Candidate_Email', 'Enter Candidate Email')}
              label={t('Candidate_Email', 'Candidate Email')}
              value={candidateEmail}
              required
              onChange={(e) => setCandidateEmail(e.target.value)}
            />
            <Input
              name="phone"
              placeholder={t('Enter_Contact', 'Enter Contact')}
              label={t('Candidate_Phone', 'Candidate Phone')}
              value={candidatePhone}
              type="number"
              onChange={(e) => setCandidatePhone(e.target.value)}
            />
          </div>

          <CustomForm
            fields={
              Array.isArray(customeFormOfSections)
                ? customeFormOfSections
                : null
            }
            onChange={handleOnChange}
            initialValues={[]}
            viewType="1"
            showFormTypes={[]}
            checkCondition={true}
            required={true}
            returnAllValues={true}
          />

          <Textarea
            name="note"
            placeholder={t('Enter_a_Note', 'Enter a Note')}
            label={t('Note', 'Note')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <FileUpload
            label={t('Resume', 'Resume')}
            maxFiles={1}
            instruction={t(
              'Drag_and_drop_or_click_to_upload_a_file',
              'Drag and drop or click to upload a file',
            )}
            accept={{
              ['*']: ['*'],
            }}
            onValidFilesDrop={(files) => {
              setResume(files);
            }}
            onFileClear={() => {
              handleAnswerChange('', '');
            }}
            required
          />
          <RadioGroup
            name="acquaintance"
            align="horizontal-fluid"
            errorText={`*${t(
              'A_selection_must_be_made_to_continue',
              'A selection must be made to continue',
            )}`}
            value={isCandidateKnown === null ? '' : isCandidateKnown}
            onChange={handleCandidateKnownChange}
          >
            <BodySmall>
              {`${t(
                'Do_you_personally_know_this_candidate',
                'Do you personally know this candidate',
              )} ?`}
              <BodySmall color="danger">{' *'}</BodySmall>
            </BodySmall>

            <RadioButton value="true">{t('Yes', 'Yes')}</RadioButton>
            <RadioButton value="false">{t('No', 'No')}</RadioButton>
          </RadioGroup>
        </PanelBody>
      )}
      <PanelFooter>
        <Button
          children={t('Discard', 'Discard')}
          type="tertiary"
          onClick={handleClose}
        />
        <Button
          children={t('Add_Referral', 'Add Referral')}
          type="primary"
          disabled={!isFormValid || loading}
          onClick={handleSubmit}
          isLoading={loading}
        />
      </PanelFooter>
    </Panel>
  );
};

export default AddReferralsPanel;
